import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { SafeArea, Scene, Size } from "../../engine/engine";
import { tapFeedback } from "../../platform/native";
import { tap, type TapRules, type TapState } from "../core/tap";
import type { GameTextures } from "./assets";

const COIN_ICON_SIZE = 48;

export class MainScene implements Scene {
  readonly view = new Container();
  private readonly button = new Graphics();
  private readonly hud = new Container();
  private readonly coinIcon = new Sprite();
  private readonly coins = new Text({ text: "", style: { fill: "#ffffff", fontSize: 48, fontWeight: "700" } });
  private readonly combo = new Text({ text: "", style: { fill: "#ffd166", fontSize: 28 } });
  private readonly onlineDot = new Graphics().circle(0, 0, 6).fill("#ffffff");
  private readonly notice = new Text({
    text: "",
    style: { fill: "#c0c4d6", fontSize: 18, align: "center", wordWrap: true, wordWrapWidth: 320 },
  });
  private pulse = 0;
  private requestRender: () => void = () => {};

  constructor(
    private state: TapState,
    private rules: TapRules,
    textures: GameTextures,
    private readonly onChange: (state: TapState) => void,
    private readonly onTapped: (earned: number, state: TapState) => void = () => {},
  ) {
    this.button.circle(0, 0, 100).fill("#ef476f");
    this.button.eventMode = "static";
    this.button.cursor = "pointer";
    this.button.on("pointertap", () => this.onTap());
    this.coinIcon.anchor.set(0, 0.5);
    this.coins.anchor.set(0, 0.5);
    this.setTextures(textures);
    this.hud.addChild(this.coinIcon, this.coins);
    this.combo.anchor.set(0.5, 0);
    this.notice.anchor.set(0.5, 1);
    this.view.addChild(this.button, this.hud, this.combo, this.onlineDot, this.notice);
    this.setConnection(true, "");
    this.render();
  }

  attach(requestRender: () => void): void {
    this.requestRender = requestRender;
  }

  setRules(rules: TapRules): void {
    this.rules = rules;
  }

  /** Swaps in new art, e.g. after an OTA content update. */
  setTextures(textures: GameTextures): void {
    this.coinIcon.texture = textures.coin;
    // Fit the longer side, so remote art of any aspect ratio keeps its shape.
    this.coinIcon.scale.set(COIN_ICON_SIZE / Math.max(textures.coin.width, textures.coin.height, 1));
    this.coins.x = this.coinIcon.width + 12;
    this.render();
  }

  /** Small online indicator plus an optional message, e.g. while reconnecting. */
  setConnection(online: boolean, message: string): void {
    this.onlineDot.tint = online ? "#06d6a0" : "#ffd166";
    this.notice.text = message;
    this.requestRender();
  }

  layout(size: Size, safe: SafeArea): void {
    this.hud.position.set(size.width / 2, safe.top + 48);
    this.combo.position.set(size.width / 2, safe.top + 84);
    this.button.position.set(size.width / 2, size.height / 2);
    this.button.scale.set(Math.min(size.width, size.height) / 400);
    this.onlineDot.position.set(size.width - safe.right - 20, safe.top + 20);
    this.notice.position.set(size.width / 2, size.height - safe.bottom - 24);
    this.notice.style.wordWrapWidth = Math.min(size.width - 32, 420);
  }

  update(deltaMs: number): boolean {
    if (this.pulse <= 0) return false;
    this.pulse = Math.max(0, this.pulse - deltaMs / 120);
    this.button.alpha = 1 - this.pulse * 0.3;
    return this.pulse > 0;
  }

  private onTap(): void {
    const result = tap(this.state, performance.now(), this.rules);
    this.state = result.state;
    this.pulse = 1;
    tapFeedback();
    this.render();
    this.onChange(this.state);
    this.onTapped(result.earned, this.state);
  }

  private render(): void {
    this.coins.text = `${this.state.coins}`;
    this.hud.pivot.x = (this.coins.x + this.coins.width) / 2;
    this.combo.text = this.state.combo > 1 ? `combo x${this.state.combo}` : "";
    this.requestRender();
  }
}
